import React, { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu } from 'lucide-react';
import SidebarNavigation from './SidebarNavigation';
import { SIDEBAR_RAIL_WIDTH_PX } from './sidebarDimensions';
import { useSidebar } from '../../contexts/SidebarContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import GymBillingBanner from '../gym/billing/GymBillingBanner';
import { gymDarkPageBg } from '../gym/gymPageTheme';

/** Sotto questa larghezza: drawer + header hamburger, sidebar chiusa di default. Sopra: sidebar fissa aperta (WEBAPP-033). */
const MOBILE_BREAKPOINT = '(max-width: 1023px)';
/** Su telefono/tablet in portrait: overlay "ruota in orizzontale". pointer: coarse evita che il PC con finestra stretta (viewport portrait) lo veda come mobile (WEBAPP-033). */
const MOBILE_PORTRAIT = '(max-width: 1023px) and (orientation: portrait) and (pointer: coarse)';

/**
 * SidebarLayout - Layout principale con sidebar verticale
 * Su desktop: sidebar fissa + main con margin.
 * Su mobile (landscape): header con hamburger + drawer + main full width.
 * Su mobile (portrait): overlay "Ruota in orizzontale" per uso ottimale.
 */
const SidebarLayout = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isCollapsed, closeMobileDrawer, isMobileDrawerOpen, toggleMobileDrawer } = useSidebar();
  const { isDark } = useTheme();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const isMobilePortrait = useMediaQuery(MOBILE_PORTRAIT);

  // Chiudi il drawer quando cambia route (navigazione da mobile)
  useEffect(() => {
    closeMobileDrawer();
  }, [location.pathname, closeMobileDrawer]);

  // Su mobile in portrait: solo messaggio "ruota in orizzontale"
  if (isMobilePortrait) {
    return (
      <div className={`fixed inset-0 z-[100] flex flex-col items-center justify-center bg-gray-100 p-6 text-center ${gymDarkPageBg}`}>
        <div className="max-w-sm space-y-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
            <svg className="w-8 h-8 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('gym.layout.rotateDevice', 'Ruota il dispositivo')}
          </h1>
          <p className="text-gray-600 dark:text-white/75">
            {t('gym.layout.rotateDeviceDesc', 'Per usare l\'app da telefono, ruota il dispositivo in orizzontale.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex h-full min-h-0 overflow-hidden transition-colors ${
        isDark ? 'bg-gray-950' : 'bg-gradient-to-r from-[#FDFDFE] to-[#E5DFF1]'
      }`}
    >
      <SidebarNavigation />

      {/* Su mobile: header con hamburger (touch target ≥ 44px) */}
      {isMobile && (
        <header className="fixed left-0 right-0 top-0 z-40 flex h-14 items-center justify-between border-b border-gray-200 bg-white px-4 dark:border-gray-800 dark:bg-gray-900">
          <button
            type="button"
            onClick={toggleMobileDrawer}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center -ml-2 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-white/10"
            aria-label={t('gym.layout.openMenu', 'Apri menu')}
            aria-expanded={isMobileDrawerOpen}
          >
            <Menu className="h-6 w-6 text-gray-700 dark:text-white" />
          </button>
          {/* Logo brand solo nel drawer: qui niente FitGymTrack (evita SVG enorme / duplicato) */}
          <div className="flex-1 min-w-0" aria-hidden="true" />
          <div className="min-w-[44px]" aria-hidden="true" />
        </header>
      )}

      {/* Main: su mobile senza margin e con padding-top per l'header fisso */}
      <main
        className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden transition-all duration-300"
        style={
          isMobile
            ? { marginLeft: 0, overscrollBehavior: 'none' }
            : {
                marginLeft: isCollapsed ? `${SIDEBAR_RAIL_WIDTH_PX}px` : 'clamp(200px, 20vw, 280px)',
                overscrollBehavior: 'none',
              }
        }
      >
        <div
          className={`min-h-0 w-full min-w-0 flex-1 overflow-y-auto overflow-x-hidden ${isMobile ? 'pt-14' : ''}`}
        >
          <GymBillingBanner />
          {children || <Outlet />}
        </div>
      </main>
    </div>
  );
};

export default SidebarLayout;

